// UI domain - React UI components for game enhancement overlay

import { APP_VERSION, BUILD_TIMESTAMP, FONT_FAMILY_CSS } from "@game/config";
import React, { Component, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LobbyBrowser } from "../lobby/LobbyBrowser";
import { LoadoutPanelContainer } from "../loadout/LoadoutPanelContainer";
import { AuthPanel } from "./AuthPanel";
import { ConnectionComponent } from "./connection.ui";
import { CTFScoreboardComponent } from "./ctf-scoreboard.ui";
import { GameEndModal } from "./game-end.ui";
import { LeaderboardComponent } from "./leaderboard.ui";
import { MinimapComponent } from "./minimap.ui";
import { PartyPanel } from "./party.ui";
import { SkillButtons } from "./skill-buttons.ui";
import { uiBridge } from "./ui.bridge";

/**
 * Check if an error should be ignored in production
 * Filters out Chrome extension errors and other external errors
 */
function shouldIgnoreError(error: Error): boolean {
  const errorMessage = error.message || "";
  const errorStack = error.stack || "";

  if (process.env.NODE_ENV === "production") {
    if (
      errorMessage.includes("chrome-extension://") ||
      errorStack.includes("chrome-extension://") ||
      errorMessage.includes("Script error") ||
      errorMessage.includes("Non-Error promise rejection") ||
      errorMessage.includes("ResizeObserver loop limit exceeded")
    ) {
      return true;
    }

    const criticalErrors = [
      "realtime",
      "game state",
      "render loop",
      "network connection",
    ];

    const isCritical = criticalErrors.some((keyword) =>
      errorMessage.toLowerCase().includes(keyword.toLowerCase()),
    );

    return !isCritical;
  }

  return false;
}

export class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    if (shouldIgnoreError(error)) {
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV === "development") {
      console.error("React UI Error:", error, errorInfo);
    }

    if (shouldIgnoreError(error)) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return React.createElement(
        "div",
        {
          style: {
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#dc2626",
            color: "white",
            padding: "10px",
            fontFamily: FONT_FAMILY_CSS,
            fontSize: "12px",
            zIndex: 10000,
          },
        },
        process.env.NODE_ENV === "development"
          ? "UI Error - Check console"
          : "UI Error - Vanilla UI active",
      );
    }

    return this.props.children;
  }
}

export const StatusBar = () => {
  const versionEl = document.getElementById("version-info");
  const buildEl = document.getElementById("build-timestamp");

  useEffect(() => {
    if (versionEl) {
      versionEl.textContent = `v${APP_VERSION}`;
    }

    if (buildEl) {
      buildEl.textContent = BUILD_TIMESTAMP;
    }
  }, [buildEl, versionEl]);

  return null;
};

export const ToastContainer = () => {
  const activeToastsRef = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const toastContainerEl = document.getElementById("toast-container");

    if (!toastContainerEl) {
      console.warn("Toast container element not found");
      return;
    }

    const interval = setInterval(() => {
      const toasts = uiBridge.getToasts();

      if (!toasts || !Array.isArray(toasts)) {
        return;
      }

      const activeToasts = activeToastsRef.current;
      const currentToastIds = new Set(toasts.map((t) => t.id));
      const existingToastIds = new Set(activeToasts.keys());

      existingToastIds.forEach((toastId) => {
        if (!currentToastIds.has(toastId)) {
          const toastEl = activeToasts.get(toastId);
          if (toastEl) {
            toastEl.classList.remove("show");
            setTimeout(() => {
              if (toastEl.parentNode) {
                toastEl.parentNode.removeChild(toastEl);
              }
              activeToasts.delete(toastId);
            }, 300);
          }
        }
      });

      toasts.forEach((toast) => {
        if (!activeToasts.has(toast.id)) {
          const toastEl = document.createElement("div");
          toastEl.className = `toast ${toast.type}`;
          toastEl.textContent = toast.message;
          toastEl.setAttribute("data-id", toast.id);

          toastContainerEl.appendChild(toastEl);
          activeToasts.set(toast.id, toastEl);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              toastEl.classList.add("show");
            });
          });
        }
      });
    }, 100);

    return () => {
      clearInterval(interval);
      const activeToasts = activeToastsRef.current;
      activeToasts.forEach((toastEl) => {
        if (toastEl.parentNode) {
          toastEl.parentNode.removeChild(toastEl);
        }
      });
      activeToasts.clear();
    };
  }, []);

  return null;
};

export const GameUI = () => {
  const [panelsContainer, setPanelsContainer] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    window.uiBridge = uiBridge;
    const container = document.getElementById("react-panels");
    setPanelsContainer(container);

    return () => {
      delete window.uiBridge;
    };
  }, []);

  return (
    <ErrorBoundary>
      <StatusBar />
      <ToastContainer />
      <LeaderboardComponent />
      <CTFScoreboardComponent />
      <ConnectionComponent />
      <MinimapComponent />
      <SkillButtons />
      <GameEndModal />
      {panelsContainer &&
        createPortal(
          <>
            <LobbyBrowser />
            <LoadoutPanelContainer />
            <AuthPanel />
            <PartyPanel />
          </>,
          panelsContainer,
        )}
    </ErrorBoundary>
  );
};

export function App() {
  return <GameUI />;
}
